import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, FormControlLabel, Grid, Radio, Tooltip, Typography, useTheme} from "@mui/material";
import {CheckCircleOutlineRounded, HighlightOffRounded} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useForm, FormProvider} from "react-hook-form";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {
	bookingDataValueState,
	counterSaleManagementValueState,
	preauthState,
	raFillingAccordionValidationState,
	recoveryRaModeState,
	rentalAgreementFillingValueState,
	stepState
} from "../state";
import {IPreAuthDataForm} from "../../models/models";
import {PRE_AUTH_FORM_INITIAL_VALUE, verifyPossibleStatusValue} from "./config";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {FormRadioGroup, FormTextField} from "../../common/form-fields-wrappers";
import {
	FINANCIAL_CREDIT_TYPES,
	FINANCIAL_PAYMENT_TIMING,
	LOCALES,
	POLLING_INTERVAL_MS,
	SegmentCode
} from "../../config";
import {snackbarTypes } from "../../../../util/state/globalVariables";
import {
	fetchBusinessContinuitySkipPayment,
	fetchRaRecoveryReceipt,
	fetchRaUploadReceipt,
	raPreAuthPaymentDispositive,
	raPreAuthPaymentInvoiceDraft,
	raPreAuthPaymentPreauthorize,
	raPreAuthPaymentVerify,
	raPreAuthPriceList,
	raPreAuthVerifyPaymentStatus,
	raUpdateByCounterSale
} from "../services/http.services";
import CustomLoader from "../../../../util/component/CustomLoader";
import {deepClone} from "../../../../util/state/commonFunctions";
import {formatCurrency, getLocaleByLang} from "../../utils";
import QrCodeDialog from "../qr-code-dialog/QrCodeDialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import {
	raDispositivePaymentReconciliation,
	raPreAuthPaymentReconciliation,
	raPreAuthPaymentReconciliationVerify
} from "../../payment-reconciliation/services/http.services";

const PreAuth = (props: { keyLess: boolean | undefined }) => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const appLanguage = useTranslation();
	const { dispatchGlobalData } = useContext(GlobalContext);

	const [verifyRefetchFlag, setVerifyRefetchFlag] = useState<boolean>(false);
	const [verifyReconciliationRefetchFlag, setVerifyReconciliationRefetchFlag] = useState<boolean>(false);
	const [secondCreditCardRequired, setSecondCreditCardRequired] = useState<boolean>(false);
	const [preAuthPriceList, setPreAuthPriceList] = useState<any>(null);
	const [preAuthAmount, setPreAuthAmount] = useRecoilState<any>(preauthState);
	const [confirmDialogTitle, setConfirmDialogTitle] = useState<string>('');
	const [confirmDialogContent, setConfirmDialogContent] = useState<string>('');
	const [confirmDialogOpen, setConfirmDialogOpen] = useState<'first' | 'second' | ''>('');
	const [receipTypeUpload, setReceipTypeUpload] = useState<"firstPreAuth" | "secondPreAuth" | "dispositive" | ''>('');
	const [qrCodeDialogTitle, setQrCodeDialogTitle] = useState<string>();

    const [bookingDataValue, setBookingDataValue] = useRecoilState(bookingDataValueState);
	const counterSaleManagement = useRecoilValue(counterSaleManagementValueState);
	const recoveryMode = useRecoilValue(recoveryRaModeState);

	const inputTypeFilesCheckRef: any = useRef({});
	let priceListPayload: any = null;

	const operationDoneValues = useMemo(() => {
		return [verifyPossibleStatusValue.ok, verifyPossibleStatusValue.okToBeReconciled];
	}, [])

	const operationSuccessDoneValues = useMemo(() => {
		return [verifyPossibleStatusValue.ok, verifyPossibleStatusValue.okToBeReconciled];
	}, [])

	// Outcomes -> {operationId: ..., qrCode: ...}
	const [outcome, setOutcome] = useState<any>({
		dispositive: null,
		firstPreAuth: null,
		secondPreAuth: null
	});
	const [outcomeFromBe, setOutcomeFromBe] = useState<any>({
		dispositive: null,
		firstPreAuth: null,
		secondPreAuth: null
	});
	useEffect(() => {
		setOutcome(outcomeFromBe);
		console.log({ outcomeFromBe })
	}, [outcomeFromBe]);

	const [verifyPaymentRecStatus, setVerifyPaymentRecStatus] = useState<any>();
	// open/close payment validation dialog
	const [openActionValidation, setOpenActionValidation] = useState<boolean>(false);
	const [actionValidation, setActionValidation] = useState<'dispositive' | 'firstPreAuth' | 'secondPreAuth' | ''>('');
	const setRaFillingAccordionValidation = useSetRecoilState(raFillingAccordionValidationState);
	// Action Status - manage view of the icons
	/**
	 NO_OPERATION - no operation performed,
	 OK - ok,
	 KO - ko,
	 OK_TO_BE_RECONCILED - ok after upload the check,
	 CANCELLED - cancelled,
	 NOT_EXPECTED - esito non atteso
	 verifyPossibleStatusValue
	 */
	const [actionStatus, setActionStatus] = useState<any>({
		dispositive: {
			byPos: verifyPossibleStatusValue.noOperation,
			byLink: verifyPossibleStatusValue.noOperation,
			cancelledOperationId: null
		},
		firstPreAuth: {
			byPos: verifyPossibleStatusValue.noOperation,
			byLink: verifyPossibleStatusValue.noOperation,
			cancelledOperationId: null
		},
		secondPreAuth: {
			byPos: verifyPossibleStatusValue.noOperation,
			byLink: verifyPossibleStatusValue.noOperation,
			cancelledOperationId: null
		}
	});

	const [billingCustomerId, setBillingCustomerId] = useState<any>(null);

	const [step, setStep] = useRecoilState(stepState);
	const bookingEntity = useRecoilValue(bookingDataValueState);
	const raFilling = useRecoilValue(rentalAgreementFillingValueState);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(appLanguage.i18n.language);
	}, [appLanguage.i18n.language]);

	const preAuthDataForm = useForm<IPreAuthDataForm>({
		defaultValues: PRE_AUTH_FORM_INITIAL_VALUE as IPreAuthDataForm
	});

	// Manage Back & Next step actions
	const handleBackStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		if (step.active > 0) {
			setStep({
				active: step.active - 1,
				completed: step.completed,
				numSteps: step.numSteps
			});

			if(!recoveryMode) {	
				setRaFillingAccordionValidation({
					generalInfo: true,
					firstBillingOwnerInfo: false,
					secondBillingOwnerInfo: false,
					ancillariesInfo: false
				});
			}
		}
	}

	const resetOutcome = () => {
		setOutcome({
			dispositive: null,
			firstPreAuth: null,
			secondPreAuth: null
		});
	}
	const resetBeOutcome = () => {
		setOutcomeFromBe({
			dispositive: null,
			firstPreAuth: null,
			secondPreAuth: null
		});
	}

	const hasDispositiveToBeReconciled = () => {
		return actionStatus.dispositive.byPos === verifyPossibleStatusValue.okToBeReconciled;
	}

	const handleNextStep = async (e: React.SyntheticEvent) => {
		e.stopPropagation();

		if (hasFailure()) {
			viewErrorMessage('preAuth.message.error.missingReceipts', snackbarTypes.warning);
			return;
		}

		if (hasToBeReconciled()) {
			await businessContinuitySkipPaymentMutation.mutateAsync({idRentalAgreement: bookingEntity.idRentalAgreement} as any);
		}

		if (!hasDispositiveToBeReconciled()) {
			await invoiceDraftMutation.mutateAsync({idRA: bookingEntity.idRentalAgreement} as any);
		}

		if (props.keyLess) {
			raUpdateByCounterSaleMutation.mutate({
				idRentalAgreement: bookingEntity.idRentalAgreement,
				keyless: true,
				preauthRentalAmount: getFirstSecondAmount('first'),
			} as any);
		} else {
			raUpdateByCounterSaleMutation.mutate({idRentalAgreement: bookingEntity.idRentalAgreement} as any);
		}
	}

	const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(message),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	/** Price list Query */
	const priceListQuery = useQuery(
		['priceList', priceListPayload],
		() => raPreAuthPriceList(priceListPayload), {
		enabled: false,
		onSuccess: (response) => {
				// get Payment Status on init PreAuth component
				verifyPaymentStatusMutation.mutate({
					idRentalAgreement:		bookingEntity.idRentalAgreement,
					firstCreditCardAmount: 	response.result.firstCard,
					secondCreditCardAmount: response.result.secondCard
				} as any);

				// set fields
				preAuthDataForm.setValue('first.suggestedPreAuthValue', formatCurrency(locale as LOCALES, response.result.firstCard));

				if (!!response.result.secondCard) {
					setSecondCreditCardRequired(true);
					preAuthDataForm.setValue('second.suggestedPreAuthValue', formatCurrency(locale as LOCALES, response.result.secondCard));
				} else {
					setSecondCreditCardRequired(false);
				}

				setPreAuthPriceList({firstCard: response.result.firstCard, secondCard: response.result.secondCard});
			},
			onError: (error: any) => {
				// get Payment Status
				verifyPaymentStatusMutation.mutate({
					idRentalAgreement:		bookingEntity.idRentalAgreement,
					firstCreditCardAmount: 	0,
					secondCreditCardAmount: 0
				} as any);

				if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"NO_SUGGESTED_PRE_AUTH\"") {
					viewErrorMessage('preAuth.message.error.noSuggestedPreAuth');
				}
				else {
					viewErrorMessage('translation:error.tryLater');
				}
			}
		})

	
	function validatePreAuth(obj: { dispositiveStatus: any; firstPreAuthStatus: any; secondPreAuthStatus: any }): boolean {
		const statuses = Object.values(obj);

		//"skippa solo se preauth 1, 2 e dispositive sono tutte OK, OK_TO_BE_RECONCILED o NOT_EXPECTED"
		//non skippare se contiene almeno uno tra ko, cancelled, no operation	  
		if (statuses.includes(verifyPossibleStatusValue.noOperation) ||
			statuses.includes(verifyPossibleStatusValue.ko) ||
			statuses.includes(verifyPossibleStatusValue.cancelled)) {
			return false;
		}
			
		return true;
	}

	/** Verify Payment Status - invocata all'atteraggio della pagina o dopo preauth/payment*/
	const verifyPaymentStatusMutation = useMutation(
		raPreAuthVerifyPaymentStatus,
		{
			onSuccess: async (response: any) => {
				let actionSts = deepClone(actionStatus);
				/** dispositiveOk/firstPreAuthOk/secondPreAuthOk:
				 * NO_OPERATION - no operation performed,
				 * OK - ok,
				 * KO - ko,
				 * OK_TO_BE_RECONCILED - ok after upload the check,
				 * CANCELLED - cancelled,
				 * NOT_EXPECTED - esito non atteso
				 */
				actionSts.dispositive.byPos = 	response.dispositiveStatus;
				actionSts.firstPreAuth.byPos = 	response.firstPreAuthStatus;
				actionSts.secondPreAuth.byPos = response.secondPreAuthStatus
				// operations id
				actionSts.firstPreAuth.cancelledOperationId = 	response.firstPreAuthCancelledOperationId;
				actionSts.secondPreAuth.cancelledOperationId = 	response.secondPreAuthCancelledOperationId;
				actionSts.dispositive.cancelledOperationId = 	response.dispositiveCancelledOperationId;

				await setActionStatus(actionSts);
				setReceipTypeUpload('');

				if (!!response.pendingPreauthOperationId) {
					if (response.pendingPreauth === 'FIRST') {
						sessionStorage.setItem('firstPreAuthRefresh', JSON.stringify({
							operationId: response.pendingPreauthOperationId,
							qrCode: response.pendingPreauthQrcode,
						}));
						resetBeOutcome();
					} else if (response.pendingPreauth === 'SECOND') {
						sessionStorage.setItem('secondPreAuthRefresh', JSON.stringify({
							operationId: response.pendingPreauthOperationId,
							qrCode: response.pendingPreauthQrcode,
						}));
						resetBeOutcome();
					}
				} else if (!!response.pendingDispositiveOperationId) {
					if (!!response.pendingDispositiveOperationId) {
						sessionStorage.setItem('dispositiveRefresh', JSON.stringify({
							operationId: response.pendingDispositiveOperationId,
							qrCode: response.pendingDispositiveQrcode,
						}));
						resetBeOutcome();
					}
				}

				resetOutcome();

				const bdv = deepClone(bookingDataValue);

				bdv.hasToBeReconciled = hasToBeReconciled();
				bdv.hasDispositiveToBeReconciled = hasDispositiveToBeReconciled();

				bdv.checkPreAuth = validatePreAuth({dispositiveStatus:response.dispositiveStatus,
					firstPreAuthStatus: response.firstPreAuthStatus,
					secondPreAuthStatus: response.secondPreAuthStatus
				}) 

				bdv.hasAnyTransaction = response.dispositiveStatus === 	verifyPossibleStatusValue.ok ||
										response.firstPreAuthStatus === verifyPossibleStatusValue.ok ||
										response.secondPreAuthStatus === verifyPossibleStatusValue.ok ||
										response.dispositiveStatus === 	verifyPossibleStatusValue.okToBeReconciled ||
										response.firstPreAuthStatus  === verifyPossibleStatusValue.okToBeReconciled ||
										response.secondPreAuthStatus  === verifyPossibleStatusValue.okToBeReconciled;
				await setBookingDataValue(bdv);
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	/** update ra by counter sale - /ra/update-by-counter-sale*/
	const raUpdateByCounterSaleMutation = useMutation(
		raUpdateByCounterSale,
		{
			onSuccess: async (response: any) => {
				// set next step
				const completed = step.completed.concat();
				completed[step.active] = true;
				setStep({
					active: step.active + 1,
					completed,
					numSteps: step.numSteps
				});
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	/** Business continuity skip payment*/
	const businessContinuitySkipPaymentMutation = useMutation(
		fetchBusinessContinuitySkipPayment,
		{
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	/** Scatta Emissione della fattura su OK del pagamento (dispositive) */
    const invoiceDraftMutation = useMutation(
        raPreAuthPaymentInvoiceDraft,
        {
            onError: (error) => {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t("preAuth.message.error.generateInvoiceDraft"),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        });

	const dispositiveMutation = useMutation(   // payByPos
		raPreAuthPaymentDispositive,
		{
			onSuccess: async (response) => {
				let outC = deepClone(outcome);
				outC.dispositive = response;
				setOutcomeFromBe(outC);
				setQrCodeDialogTitle(t('preAuth.qrCodeDialog.title'));
				setOpenActionValidation(true);
				setVerifyRefetchFlag(true);
                sessionStorage.setItem('dispositiveRefresh', JSON.stringify(response));
			},
			onError: (error) => {
				setActionValidation('');
				viewErrorMessage('translation:error.tryLater');
			}
		});

	const actionVerifyQuery = useQuery({
		// Refetch the data every interval
		refetchInterval: POLLING_INTERVAL_MS,
		enabled: verifyRefetchFlag,
		queryKey: ['verify', {operationId: !!outcome[actionValidation]?.operationId ? outcome[actionValidation].operationId : 'ND'}],
		queryFn: async () => raPreAuthPaymentVerify({operationId: !!outcome[actionValidation]?.operationId ? outcome[actionValidation].operationId : ''}),
		onSuccess: async (response: any) => {
			if (response.status === 'OK') {
				await setVerifyRefetchFlag(false);
				sessionStorage.removeItem(actionValidation + 'Refresh');
			}
			else if (response.status === 'CANCELLED') {
				await setVerifyRefetchFlag(false);
				sessionStorage.removeItem(actionValidation + 'Refresh');
			}

			if (response.status === 'OK' || response.status === 'CANCELLED') {
				setQrCodeDialogTitle('');
				setOpenActionValidation(false);
				verifyPaymentStatusMutation.mutate({
					idRentalAgreement: bookingEntity.idRentalAgreement,
					firstCreditCardAmount: !!preAuthAmount.firstPreAuth ? preAuthAmount.firstPreAuth : 0,
					secondCreditCardAmount: !!preAuthAmount.secondPreAuth ? preAuthAmount.secondPreAuth : 0
				} as any);
			}
		},
		onError: (error: any) => {
			setQrCodeDialogTitle('');
			setOpenActionValidation(false);
			setVerifyRefetchFlag(false);
			viewErrorMessage('translation:error.tryLater');
		}
	})

	/**
	 * Verify Reconciliation operation status polling
	 */
	const actionVerifyReconciliationQuery = useQuery({
		// Refetch the data every interval
		refetchInterval: POLLING_INTERVAL_MS,
		enabled: verifyReconciliationRefetchFlag,
		queryKey: ['verifyReconciliation', {
			operationId: verifyPaymentRecStatus?.operationId
		}],
		queryFn: async () => raPreAuthPaymentReconciliationVerify({operationId: verifyPaymentRecStatus?.operationId}),
		onSuccess: async (response: any) => {
			if (response.status === 'OK' || response.status === 'CANCELLED') {
				await setVerifyReconciliationRefetchFlag(false);
				setQrCodeDialogTitle('');
				setOpenActionValidation(false);

				verifyPaymentStatusMutation.mutate({
					idRentalAgreement: bookingEntity.idRentalAgreement,
					firstCreditCardAmount: !!preAuthAmount.firstPreAuth ? preAuthAmount.firstPreAuth : 0,
					secondCreditCardAmount: !!preAuthAmount.secondPreAuth ? preAuthAmount.secondPreAuth : 0
				} as any);

				setVerifyPaymentRecStatus(null);
			}
		},
		onError: (error: any) => {
			viewErrorMessage('translation:error.tryLater');

			setQrCodeDialogTitle('');
			setOpenActionValidation(false);
			setVerifyReconciliationRefetchFlag(false);
			setVerifyPaymentRecStatus(null);
		}
	})

	/** Check payment made  */
	const preauthorizeMutation = useMutation(
		raPreAuthPaymentPreauthorize,
		{
			onSuccess: async (response) => {
				let outC = deepClone(outcome);
				outC[actionValidation] = response;
				await setOutcomeFromBe(outC);

				setQrCodeDialogTitle(t('preAuth.qrCodeDialog.title'));
				setOpenActionValidation(true);
				setVerifyRefetchFlag(true);
				sessionStorage.setItem(actionValidation + 'Refresh', JSON.stringify(response));
			},
			onError: (error) => {
				setActionValidation('');
				viewErrorMessage('translation:error.tryLater');
			}
		});

	const raUpdateReceiptMutation = useMutation(
		fetchRaUploadReceipt,
		{
			onSuccess: async (response) => {
				// verify payment status
				verifyPaymentStatusMutation.mutate({
					idRentalAgreement:		bookingEntity.idRentalAgreement,
					firstCreditCardAmount: 	!!preAuthAmount.firstPreAuth ? preAuthAmount.firstPreAuth : 0,
					secondCreditCardAmount: !!preAuthAmount.secondPreAuth ? preAuthAmount.secondPreAuth : 0
				} as any);
			},
			onError: (error) => {
				viewErrorMessage('translation:error.tryLater');
			}
		});

	/**
	 * onSuccess callback of PreAuth Reconciliation
	 */
	const onSuccessPreAuthReconciliation = (data: {operationId: string, qrCode: string}) => {
		setVerifyPaymentRecStatus(data);

		switch (receipTypeUpload) {
			case "firstPreAuth":
				setQrCodeDialogTitle(t("paymentReconciliation.paymentReconciliationDialog.title.firstPreAuth"));
				break;
			case "secondPreAuth":
				setQrCodeDialogTitle(t("paymentReconciliation.paymentReconciliationDialog.title.secondPreAuth"));
				break;
			case "dispositive":
				setQrCodeDialogTitle(t("paymentReconciliation.paymentReconciliationDialog.title.payment"));
				break;
		}

		let outC = deepClone(outcome);
		outC[receipTypeUpload] = {qrCode: data.qrCode};
		setOutcomeFromBe(outC);

		setOpenActionValidation(true);
		setVerifyReconciliationRefetchFlag(true);
	}

	/**
	 * Get info for PreAuth Payment Reconciliation
	 */
	const raPreAuthPaymentReconciliationMutation = useMutation(
		raPreAuthPaymentReconciliation,
		{
			onSuccess: async (response) => onSuccessPreAuthReconciliation(response),
			onError: (error) => {
				viewErrorMessage('translation:error.tryLater');
			}
		});

	/**
	 * Get info for Dispositive Payment Reconciliation
	 */
	const raDispositivePaymentReconciliationMutation = useMutation(
		raDispositivePaymentReconciliation,
		{
			onSuccess: async (response) => onSuccessPreAuthReconciliation(response),
			onError: (error) => {
				viewErrorMessage('translation:error.tryLater');
			}
		});

	const fetchRaRecoveryReceiptMutation = useMutation(
		fetchRaRecoveryReceipt,
		{
			onSuccess: async (response) => {
				if (receipTypeUpload !== 'dispositive') {
					let amount = 0;
					switch(receipTypeUpload) {
						case 'firstPreAuth':
							amount = getFirstSecondAmount('first');
							break;
						case 'secondPreAuth':
							amount = getFirstSecondAmount('second');
							break;
					}

					// @ts-ignore
					preAuthAmount[receipTypeUpload] = amount;
					await setPreAuthAmount(preAuthAmount);

					const payload = {
						rentalId: bookingEntity.idRentalAgreement,
						amount: {
							currency: "EUR",
							value: amount
						},
						paymentChannel: "POS",
						tokenizeCard: true,
						type: receipTypeUpload === "firstPreAuth" ? "FIRST" : "SECOND"
					};

					raPreAuthPaymentReconciliationMutation.mutate(payload);
				} else {
					// dispositive
					const totalToPay = counterSaleManagement?.last?.promoRentalAmountVatIncludedTotal === 0 ? counterSaleManagement?.last?.totalAmountVatIncluded :  counterSaleManagement?.last?.promoRentalAmountVatIncludedTotal;
					const payload = {
						rentalId: bookingEntity.idRentalAgreement,
						amount: {
							currency: "EUR",
							value: totalToPay
						},
						paymentChannel: "POS"
					}
					raDispositivePaymentReconciliationMutation.mutate(payload);
				}
			},
			onError: (error) => {
				viewErrorMessage('translation:error.tryLater');
			}
		});

	const getFirstSecondAmount = (type: 'first' | 'second') => {
		const typeFormValue = preAuthDataForm.getValues(type);
		let amount = null;
		if (typeFormValue.preAuthChoice === "SUGGESTED") {
			amount = !!preAuthPriceList?.firstCard ? preAuthPriceList.firstCard : null;
		} else {
			amount = typeFormValue.alternativePreAuthValue;
			amount = parseFloat(amount.replace(',', '.'));
			if (!isNaN(amount)) {
				amount = amount * 100;
			}
		}

		if (amount === null) {
			dispatchGlobalData({
				type: 'SET_SNACKBAR',
				payload: {
					snackbar: {
						message: t('preAuth.message.error.alternativeMethod'),
						type: snackbarTypes.error,
						show: true,
						autoClose: true,
					}
				}
			});
		}else{
			amount = Math.floor(amount);
		}

		return amount;
	}

// FIXME:  da fixare prendere cbCode da billinOwner, mettere mess e bloccare (vediamo nell pomm dopo la riunione con Drivalia)
// poi con roberto portare le 3 modifiche su int e po su cert (su in andra la versione non completa ma la versione che andra su cert)
	const buildPayPreAuthPayload = (amount: number, tokenizeCard?: boolean, type?: "firstPreAuth" | "secondPreAuth") => {
		let payload: any =  {
			rentalId: bookingEntity.idRentalAgreement,
			amount: {
				currency: "EUR", // TODO: verificare currency
				value: amount
			},
			customerId: billingCustomerId,
			paymentChannel: "POS",
		}

		if (tokenizeCard !==  undefined) {
			payload['tokenizeCard'] = tokenizeCard;
		}

		if (type !==  undefined) {
			payload['type'] = type === "firstPreAuth" ? "FIRST" : "SECOND";
		}

		return payload;
	}

	/**
	 * Pay By Pos
	 */
	const handlePayByPos = async (e: React.SyntheticEvent) => {
		e.stopPropagation();

		await setActionValidation('dispositive');
		const totalToPay = counterSaleManagement?.last?.promoRentalAmountVatIncludedTotal === 0 ? counterSaleManagement?.last?.totalAmountVatIncluded :  counterSaleManagement?.last?.promoRentalAmountVatIncludedTotal;
		dispositiveMutation.mutate(buildPayPreAuthPayload(totalToPay) as any);
	}

	/**
	 * PreAuth
	 */
	const handlePreAuthByPos = async (e: React.SyntheticEvent, type: "firstPreAuth" | "secondPreAuth") => {
		let amount = null;
		if (type === "firstPreAuth") {
			amount = getFirstSecondAmount('first');
		}
		else {
			amount = getFirstSecondAmount('second');
		}

		if (amount === null) {
			return;
		}

		await setPreAuthAmount({
			...preAuthAmount,
			[type]: amount,
		});
		await setActionValidation(type);
		preauthorizeMutation.mutate(buildPayPreAuthPayload(amount, true, type) as any);
	}

	const handleLoadRecoveryCheck = (e: React.SyntheticEvent, type: "firstPreAuth" | "secondPreAuth" | "dispositive") => {
		setReceipTypeUpload(type);
		// @ts-ignore
		inputTypeFilesCheckRef.current.click();
	}

	const handleFileCheckOnChange = (e: React.BaseSyntheticEvent) => {
		if (!!e.target.files[0]?.size && !!receipTypeUpload) {
			if (recoveryMode) { 									// se serverà distinguere i contrini utilizzare receipTypeUpload
				const form = new FormData();
				form.append('file', e.target.files[0]);

				const uploadReceiptReq: any = {
					idRentalAgreement: encodeURIComponent(bookingEntity.idRentalAgreement),
					type: 'RECEIPT',
					info: receipTypeUpload, //"firstPreAuth" | "secondPreAuth" | "dispositive"
					receipt: form
				};

				fetchRaRecoveryReceiptMutation.mutate(uploadReceiptReq);
			} else if (!!receipTypeUpload) { // skip payment per reconciliation
				const form = new FormData();
				form.append('receipt', e.target.files[0]);

				const uploadReceiptReq: any = {
					idRentalAgreement: encodeURIComponent(bookingEntity.idRentalAgreement),
					operationId: actionStatus[receipTypeUpload].cancelledOperationId,
					operationType: 'PREAUTHORIZE',
					receipt: form
				};

				raUpdateReceiptMutation.mutate(uploadReceiptReq);
			}

			inputTypeFilesCheckRef.current.value = null;
		}
	}

	const handleSkipPayment = (e: React.SyntheticEvent, type: "firstPreAuth" | "secondPreAuth" | "dispositive") => {
		setConfirmDialogTitle('preAuth.preAuthInfo.title');
		setConfirmDialogContent('preAuth.message.notify.areYouSureSkipPayment');
		setConfirmDialogOpen('first');
		setReceipTypeUpload(type);
	}

	const handleCloseConfirmDialog = (e: React.SyntheticEvent, opr: 'close' | 'confirm' | 'cancel') => {
		e.stopPropagation();

		if (opr === 'confirm') {
			switch (confirmDialogOpen) {
				case 'first':
					setConfirmDialogTitle('preAuth.preAuthInfo.title');
					setConfirmDialogContent('preAuth.message.notify.skipPaymentConseguenze');
					setConfirmDialogOpen('second');
					return;
				case 'second':
					// @ts-ignore
					inputTypeFilesCheckRef.current.click();
			}
		}

		setConfirmDialogTitle('');
		setConfirmDialogContent('');
		setConfirmDialogOpen('');
	}

	const hasFailure = () => {
		return actionStatus.dispositive.byPos === verifyPossibleStatusValue.cancelled ||
				actionStatus.firstPreAuth.byPos === verifyPossibleStatusValue.cancelled ||
				actionStatus.secondPreAuth.byPos === verifyPossibleStatusValue.cancelled;
	}

	const hasToBeReconciled = () => {
		return actionStatus.dispositive.byPos === verifyPossibleStatusValue.okToBeReconciled ||
				actionStatus.firstPreAuth.byPos === verifyPossibleStatusValue.okToBeReconciled ||
				actionStatus.secondPreAuth.byPos === verifyPossibleStatusValue.okToBeReconciled;
	}

	useEffect(() => {
		if ([SegmentCode.replacement, SegmentCode.corporate, SegmentCode.tourOperator].includes(bookingEntity.financialInfo.segment) &&
			bookingEntity.financialInfo.creditType === FINANCIAL_CREDIT_TYPES.fullCredit)
		{ // se full credit go to preview directly
			if (step.active < (step.numSteps - 1)) {
				const completed = step.completed.concat([]);
				completed[step.active] = true;
				setStep({
					active: step.active + 1,
					completed,
					numSteps: step.numSteps
				});
			}
		} else {
            if (sessionStorage.getItem('firstPreAuthRefresh')) {
                const firstPreAuthRefresh = JSON.parse(sessionStorage.getItem('firstPreAuthRefresh') as string);
                if (!!firstPreAuthRefresh) {
                    setActionValidation('firstPreAuth');
                    let outC = deepClone(outcome);
                    outC['firstPreAuth'] = firstPreAuthRefresh;
                    setOutcome(outC);

                    const ps = deepClone(actionStatus);
                    ps['firstPreAuth'].doneBP = true;
                    setActionStatus(ps);

					setQrCodeDialogTitle(t('preAuth.qrCodeDialog.title'));
                    setOpenActionValidation(true);
                    setVerifyRefetchFlag(true);
                }
            } else if (sessionStorage.getItem('secondPreAuthRefresh')) {
                const secondPreAuthRefresh = JSON.parse(sessionStorage.getItem('secondPreAuthRefresh') as string);
                if (!!secondPreAuthRefresh) {
                    setActionValidation('secondPreAuth');
                    let outC = deepClone(outcome);
                    outC['secondPreAuth'] = secondPreAuthRefresh;
                    setOutcome(outC);

                    const ps = deepClone(actionStatus);
                    ps['secondPreAuth'].doneBP = true;
                    setActionStatus(ps);

					setQrCodeDialogTitle(t('preAuth.qrCodeDialog.title'));
                    setOpenActionValidation(true);
                    setVerifyRefetchFlag(true);
                }
            } else if (sessionStorage.getItem('dispositiveRefresh')) {
                const dispositiveRefresh = JSON.parse(sessionStorage.getItem('dispositiveRefresh') as string);
                if (!!dispositiveRefresh) {
                    setActionValidation('dispositive');
                    let outC = deepClone(outcome);
                    outC['dispositive'] = dispositiveRefresh;
                    setOutcome(outC);

                    const ps = deepClone(actionStatus);
                    ps['dispositive'].doneBP = true;
                    setActionStatus(ps);

					setQrCodeDialogTitle(t('preAuth.qrCodeDialog.title'));
                    setOpenActionValidation(true);
                    setVerifyRefetchFlag(true);
                }
            } else if (!!outcome.dispositive || !!outcome.firstPreAuth || !!outcome.secondPreAuth) {
				setActionValidation(!!outcome.dispositive ? 'dispositive' : !!outcome.firstPreAuth ? 'firstPreAuth' : 'secondPreAuth');

				setQrCodeDialogTitle(t('preAuth.qrCodeDialog.title'));
				setOpenActionValidation(true);
				setVerifyRefetchFlag(true);
			} else {
				// PreAuth Info
				priceListPayload = {
					idRentalAgreement: 	bookingEntity.idRentalAgreement,
					acriss: 			raFilling.vehicleDetails.acrissCode,
					sipp: 				raFilling.vehicleDetails.sippCode,
					creditType: 		'SC', //bookingEntity.financialInfo.creditType,
					paramType: 			'PRODUCT',
					paramValue: 		bookingEntity.financialInfo.product
				};
				priceListQuery.refetch();
			}

			// Payment Info
			preAuthDataForm.setValue('billingAcriss', raFilling.generalInfo?.billingCarGroup);
			preAuthDataForm.setValue('paymentTiming', FINANCIAL_PAYMENT_TIMING[bookingEntity.financialInfo?.paymentTiming]);
			preAuthDataForm.setValue('segment', !!bookingEntity.financialInfo?.segmentDescription ? bookingEntity.financialInfo.segmentDescription : '');
			preAuthDataForm.setValue('category', !!bookingEntity.financialInfo?.categoryDescription ? bookingEntity.financialInfo.categoryDescription : '');
			preAuthDataForm.setValue('product', !!bookingEntity.financialInfo?.productDescription ? bookingEntity.financialInfo.productDescription : '');
			preAuthDataForm.setValue('bookedRentalAmount', formatCurrency(locale as LOCALES, counterSaleManagement?.last?.rentalAmountVatIncluded + counterSaleManagement?.last?.upsellAmountVatIncluded));
			preAuthDataForm.setValue('bookedAncillaries', formatCurrency(locale as LOCALES, counterSaleManagement?.last?.totalAncillaryAmount.promoAmountVatIncluded ?? counterSaleManagement?.last?.totalAncillaryAmount.amountVatIncluded));
			preAuthDataForm.setValue('bookedTotalAmount',  counterSaleManagement?.last?.promoRentalAmountVatIncludedTotal === 0 ? formatCurrency(locale as LOCALES, counterSaleManagement?.last?.totalAmountVatIncluded) : formatCurrency(locale as LOCALES, counterSaleManagement?.last?.promoRentalAmountVatIncludedTotal));

			setBillingCustomerId(raFilling.billingOwnerInfo.first.person.customerId);
		}
	}, [outcomeFromBe])

	//console.log("is keyless", props.keyLess)

	return (
		<>
			<FormProvider {...preAuthDataForm}>
				<Grid container columnSpacing={2}>
					<Grid container item xs={12} columnSpacing={2} sx={{my: theme.spacing(4)}}>
						<Grid item xs={12}>
							<Typography variant={"h6"}>
								{t("preAuth.preAuthInfo.title")}
							</Typography>
						</Grid>

						<Grid item xs={12} sx={{mt: theme.spacing(1)}}>
							<Typography variant={"bodyLarge"}>
								{t("preAuth.preAuthInfo.subtitle.creditCard.first")}
							</Typography>
						</Grid>

						<Grid item xs={12} md={6}>

							<FormRadioGroup<IPreAuthDataForm>
								name="first.preAuthChoice"
								label={''}
								sx={{width: '100%'}}
								disabled={operationDoneValues.includes(actionStatus.firstPreAuth.byPos)}
							>
								<Grid container columnSpacing={2} sx={{width: '100%'}}>
									<Grid item xs={6} sx={{display: "flex", px: theme.spacing(1)}}>
										<FormControlLabel
											value="SUGGESTED"
											label={''}
											control={<Radio />}
										/>

										<FormTextField<IPreAuthDataForm>
											name='first.suggestedPreAuthValue'
											control={preAuthDataForm.control}
											label={t("preAuth.form.label.suggestedPreAuthAmount")}
											fullWidth
											disabled
										/>

									</Grid>
									<Grid item xs={6} sx={{display: "flex", px: theme.spacing(1)}}>
										<FormControlLabel
											value="ALTERNATIVE"
											label={''}
											control={<Radio />}
										/>

										<FormTextField<IPreAuthDataForm>
											name='first.alternativePreAuthValue'
											control={preAuthDataForm.control}
											label={t("preAuth.form.label.alternativePreAuthAmount")}
											fullWidth
											disabled={operationDoneValues.includes(actionStatus.firstPreAuth.byPos)}
										/>
									</Grid>
								</Grid>
							</FormRadioGroup>
						</Grid>

						<Grid item xs={12} md={6} sx={{display: "flex", justifyContent: "flex-start",  alignItems: "flex-end", height: '48px'}}>
                            {
                                recoveryMode && !operationSuccessDoneValues.includes(actionStatus.firstPreAuth.byPos) &&
                                <Tooltip title={t('preAuth.form.tooltip.button.loadRecoveryCheck')}>
                                    <Button color={'primary'}
                                            variant={'outlined'}
                                            sx={{mx: theme.spacing(2)}}
                                            onClick={(e: React.SyntheticEvent) => handleLoadRecoveryCheck(e, 'firstPreAuth')}
                                    >
                                        {t('preAuth.form.button.uploadReceipt')}
                                    </Button>
                                </Tooltip>
                            }

                            {
                                !recoveryMode && !props.keyLess &&
                                <Button color={'primary'}
                                        sx={{mx: theme.spacing(2)}}
                                        onClick={(e: React.SyntheticEvent) => handlePreAuthByPos(e, 'firstPreAuth')}
                                        disabled={operationDoneValues.includes(actionStatus.firstPreAuth.byPos)}
                                >
                                    {t('preAuth.form.button.preAuthByPos')}
                                </Button>
                            }

							{/*<Button color={'primary'} sx={{mx: theme.spacing(2)}}>*/}
							{/*	{t('preAuth.form.button.preAuthByLink')}*/}
							{/*</Button>*/}

							{
								operationSuccessDoneValues.includes(actionStatus.firstPreAuth.byPos) &&
								<CheckCircleOutlineRounded sx={{color: theme.palette.success.main, mx: theme.spacing(2)}} />
							}

							{
								!recoveryMode && actionStatus.firstPreAuth.byPos === verifyPossibleStatusValue.cancelled &&
								<>
									<HighlightOffRounded sx={{color: theme.palette.error.main, mx: theme.spacing(2)}} />
									<Tooltip title={t('preAuth.form.tooltip.button.skipPayment')}>
										<Button color={'primary'}
											variant={'outlined'}
												sx={{mx: theme.spacing(2)}}
												onClick={(e: React.SyntheticEvent) => handleSkipPayment(e, 'firstPreAuth')}
										>
											{t('preAuth.form.button.skipPayment')}
										</Button>
									</Tooltip>
								</>
							}
						</Grid>

						{
							secondCreditCardRequired &&
							<>
								<Grid item xs={12} sx={{mt: theme.spacing(2)}}>
									<Typography variant={"bodyLarge"}>
										{t("preAuth.preAuthInfo.subtitle.creditCard.second")}
									</Typography>
								</Grid>

								<Grid item xs={12} md={6}>

									<FormRadioGroup<IPreAuthDataForm>
										name="second.preAuthChoice"
										label={''}
										sx={{width: '100%'}}
										disabled={!operationDoneValues.includes(actionStatus.firstPreAuth.byPos) || operationDoneValues.includes(actionStatus.secondPreAuth.byPos)}
									>
										<Grid container columnSpacing={2} sx={{width: '100%'}}>
											<Grid item xs={6} sx={{display: "flex", px: theme.spacing(1)}}>
												<FormControlLabel
													value="SUGGESTED"
													label={''}
													control={<Radio />}
												/>

												<FormTextField<IPreAuthDataForm>
													name='second.suggestedPreAuthValue'
													control={preAuthDataForm.control}
													label={t("preAuth.form.label.suggestedPreAuthAmount")}
													fullWidth
													disabled
												/>
											</Grid>
											<Grid item xs={6} sx={{display: "flex", px: theme.spacing(1)}}>
												<FormControlLabel
													value="ALTERNATIVE"
													label={''}
													control={<Radio />}
												/>

												<FormTextField<IPreAuthDataForm>
													name='second.alternativePreAuthValue'
													control={preAuthDataForm.control}
													label={t("preAuth.form.label.alternativePreAuthAmount")}
													fullWidth
													disabled={!operationDoneValues.includes(actionStatus.firstPreAuth.byPos) || operationDoneValues.includes(actionStatus.secondPreAuth.byPos)}
												/>
											</Grid>
										</Grid>
									</FormRadioGroup>
								</Grid>

								<Grid item xs={12} md={6} sx={{display: "flex", justifyContent: "flex-start",  alignItems: "flex-end", height: '48px'}}>
									{
										recoveryMode && !operationSuccessDoneValues.includes(actionStatus.secondPreAuth.byPos) &&
										<Tooltip title={t('preAuth.form.tooltip.button.loadRecoveryCheck')}>
											<Button color={'primary'}
													variant={'outlined'}
													sx={{mx: theme.spacing(2)}}
													onClick={(e: React.SyntheticEvent) => handleLoadRecoveryCheck(e, 'secondPreAuth')}
											>
												{t('preAuth.form.button.uploadReceipt')}
											</Button>
										</Tooltip>
									}

									{
										!recoveryMode && !props.keyLess &&
										<Button color={'primary'} sx={{mx: theme.spacing(2)}}
												onClick={(e: React.SyntheticEvent) => handlePreAuthByPos(e, 'secondPreAuth')}
												disabled={!operationDoneValues.includes(actionStatus.firstPreAuth.byPos) ||
															operationDoneValues.includes(actionStatus.secondPreAuth.byPos)}>
											{t('preAuth.form.button.preAuthByPos')}
										</Button>
									}

									{/*<Button color={'primary'} sx={{mx: theme.spacing(2)}}>*/}
									{/*	{t('preAuth.form.button.preAuthByLink')}*/}
									{/*</Button>*/}

									{
										operationSuccessDoneValues.includes(actionStatus.secondPreAuth.byPos) &&
										<CheckCircleOutlineRounded sx={{color: theme.palette.success.main, mx: theme.spacing(2)}} />
									}

									{
										!recoveryMode && actionStatus?.secondPreAuth?.byPos === verifyPossibleStatusValue.cancelled &&
										<>
											<HighlightOffRounded sx={{color: theme.palette.error.main, mx: theme.spacing(2)}} />
											<Tooltip title={t('preAuth.form.tooltip.button.skipPayment')}>
												<Button color={'primary'}
														variant={'outlined'}
														sx={{mx: theme.spacing(2)}}
														onClick={(e: React.SyntheticEvent) => handleSkipPayment(e, 'secondPreAuth')}
												>
													{t('preAuth.form.button.skipPayment')}
												</Button>
											</Tooltip>
										</>
									}
								</Grid>
							</>
						}
					</Grid>

					{
						bookingEntity.financialInfo?.segment === SegmentCode.retail &&
						bookingEntity.financialInfo?.paymentTiming === FINANCIAL_PAYMENT_TIMING.poa &&
						<>
							<Grid item xs={12}>
								<Typography variant={"h6"} sx={{mb: 1}}>
									{t('preAuth.paymentInfo.title')}
								</Typography>
							</Grid>

							<Grid container item xs={12} md={6} columnSpacing={2}>
								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='billingAcriss'
										control={preAuthDataForm.control}
										label={t('preAuth.form.label.billingAcriss')}
										fullWidth
										disabled
									/>
								</Grid>
								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='paymentTiming'
										control={preAuthDataForm.control}
										label={t("preAuth.form.label.paymentTiming")}
										fullWidth
										disabled
									/>
								</Grid>

								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='segment'
										control={preAuthDataForm.control}
										label={t("preAuth.form.label.segment")}
										fullWidth
										disabled
									/>
								</Grid>

								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='category'
										control={preAuthDataForm.control}
										label={t("preAuth.form.label.category")}
										fullWidth
										disabled
									/>
								</Grid>

								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='product'
										control={preAuthDataForm.control}
										label={t("preAuth.form.label.product")}
										fullWidth
										disabled
									/>
								</Grid>

								<Grid item xs={6}>
									{/*	<FormTextField<IPreAuthDataForm>*/}
									{/*		name='surchargeUpsell'*/}
									{/*		control={preAuthDataForm.control}*/}
									{/*		label={t("preAuth.form.label.surchargeUpsell")}*/}
									{/*		fullWidth*/}
									{/*		disabled*/}
									{/*	/>*/}
								</Grid>
							</Grid>

							<Grid container item xs={12} md={6} >
								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='bookedRentalAmount'
										control={preAuthDataForm.control}
										label={t("preAuth.form.label.bookedRentalAmount")}
										fullWidth
										disabled
									/>
								</Grid>

								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='bookedAncillaries'
										control={preAuthDataForm.control}
										label={t("preAuth.form.label.bookedAncillaries")}
										fullWidth
										disabled
									/>
								</Grid>

								<Grid item xs={6}>
									<FormTextField<IPreAuthDataForm>
										name='bookedTotalAmount'
										control={preAuthDataForm.control}
										label={t("preAuth.form.label.bookedTotalAmount")}
										fullWidth
										disabled
									/>
								</Grid>

								<Grid item xs={6} />

								<Grid item xs={12} md={6} sx={{display: "flex", justifyContent: "flex-start",  alignItems: "flex-end", height: '48px'}}>
									{
										recoveryMode && !operationSuccessDoneValues.includes(actionStatus.dispositive.byPos) &&
										<Tooltip title={t('preAuth.form.tooltip.button.loadRecoveryCheck')}>
											<Button color={'primary'}
													variant={'outlined'}
													sx={{mx: theme.spacing(2)}}
													onClick={(e: React.SyntheticEvent) => handleLoadRecoveryCheck(e, 'dispositive')}
											>
												{t('preAuth.form.button.uploadReceipt')}
											</Button>
										</Tooltip>
									}

									{
										!recoveryMode && !props.keyLess &&
										<Button color={'primary'}
												sx={{mx: theme.spacing(2)}}
												onClick={handlePayByPos}
												disabled={!operationDoneValues.includes(actionStatus.firstPreAuth.byPos) ||
													(secondCreditCardRequired && !operationDoneValues.includes(actionStatus.secondPreAuth.byPos)) ||
													operationDoneValues.includes(actionStatus.dispositive.byPos)}
										>
											{t('preAuth.form.button.payByPos')}
										</Button>
									}

									{/*<Button color={'primary'} sx={{mx: theme.spacing(2)}}>*/}
									{/*	{t('preAuth.form.button.payByLink')}*/}
									{/*</Button>*/}

									{
										operationSuccessDoneValues.includes(actionStatus.dispositive.byPos) &&
										<CheckCircleOutlineRounded sx={{color: theme.palette.success.main, mx: theme.spacing(2)}} />
									}

									{
										!recoveryMode && actionStatus.dispositive.byPos === verifyPossibleStatusValue.cancelled &&
										<>
											<HighlightOffRounded sx={{color: theme.palette.error.main, mx: theme.spacing(2)}} />
											<Tooltip title={t('preAuth.form.tooltip.button.skipPayment')}>
												<Button color={'primary'}
														variant={'outlined'}
														sx={{mx: theme.spacing(2)}}
														onClick={(e: React.SyntheticEvent) => handleSkipPayment(e, 'dispositive')}
												>
													{t('preAuth.form.button.skipPayment')}
												</Button>
											</Tooltip>
										</>
									}
								</Grid>
							</Grid>
						</>
					}
				</Grid>
			</FormProvider>

			<Box sx={{display: 'flex', justifyContent: 'space-between', mt: theme.spacing(2)}}>
				{
					!!bookingDataValue.hasAnyTransaction ?
					<Box></Box> :
					<Button variant={'text'} color={"primary"}
							onClick={handleBackStep}>
						{t('common.button.backStep')}
					</Button>
				}
				{
					step.active === (step.numSteps - 1) ?
					<Box></Box> :
					<Button variant={'outlined'} color={"accent"}
							disabled={(!operationSuccessDoneValues.includes(actionStatus.firstPreAuth.byPos) ||
										(secondCreditCardRequired && !operationSuccessDoneValues.includes(actionStatus.secondPreAuth.byPos)) ||
										!operationSuccessDoneValues.includes(actionStatus.dispositive.byPos)
										) && !props.keyLess}
							onClick={handleNextStep}
						>
						{t('common.button.nextStep')}
					</Button>
				}
			</Box>

			{
				<QrCodeDialog
					size={256}
					dialogTitle={qrCodeDialogTitle}
					spinner={verifyRefetchFlag || verifyReconciliationRefetchFlag}
					backdropClick={false}
					viewQrCodeString={true}
					open={openActionValidation}
					qrCode={outcome[actionValidation]?.qrCode ?? outcome[receipTypeUpload]?.qrCode ?? ''}
				/>
			}

			<ConfirmDialog title={confirmDialogTitle} content={confirmDialogContent} open={confirmDialogOpen} handleClose={handleCloseConfirmDialog} />

            <CustomLoader loading={dispositiveMutation.isLoading  || preauthorizeMutation.isLoading ||
                                    invoiceDraftMutation.isLoading || priceListQuery.isFetching ||
                                    verifyPaymentStatusMutation.isLoading || raUpdateByCounterSaleMutation.isLoading ||
                                    raUpdateReceiptMutation.isLoading || businessContinuitySkipPaymentMutation.isLoading ||
									fetchRaRecoveryReceiptMutation.isLoading || raDispositivePaymentReconciliationMutation.isLoading ||
									raPreAuthPaymentReconciliationMutation.isLoading} />

            <input hidden accept="image/*"
                   type="file"
                   ref={el => inputTypeFilesCheckRef.current = el}
                   onChange={(e: React.BaseSyntheticEvent) => handleFileCheckOnChange(e)}
            />
		</>
	);
}

export default PreAuth;

const ConfirmDialog = ({title, content, open, handleClose}: {title: string, content: string, open: 'first' | 'second' | '', handleClose: Function}) => {
	const theme = useTheme();
	const {t} = useTranslation(['network']);

	return (
		<Dialog
			open={open === 'first' || open === 'second'}
			onClose={(e: React.SyntheticEvent) => handleClose(e, 'close')}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">
				{t(title)}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					{t(content)}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button variant={"outlined"} color={'secondary'} onClick={(e: React.SyntheticEvent) => handleClose(e, 'cancel')}>{t('preAuth.buttons.cancel')}</Button>
				<Button onClick={(e: React.SyntheticEvent) => handleClose(e, 'confirm')}>{t('preAuth.buttons.confirm')}</Button>
			</DialogActions>
		</Dialog>
	)
}